import { useCallback, useEffect, useRef, useState } from 'react';

import { IrlEventTrackingEvent } from 'src/gql/graphql';
import { useCompleteAccountModal } from 'src/components/modals/complete-account';
import { useCreateEnterpriseDposCustomer } from './use-create-enterprise-dpos-customer';
import { useIrlEventTracking } from './use-irl-event-tracking';

export type AuthOptions = {
  authEvent: IrlEventTrackingEvent.login | IrlEventTrackingEvent.signup;
  skipNavigation?: boolean;
};

type PendingAuthEvent = AuthOptions | null;

type EnsureDposCustomerResult = {
  success: boolean;
  didAutoEnroll: boolean;
};

type UseIrlEventAuthParams = {
  dispensaryId?: string;
  enterpriseId: string;
  isoRegionCode: string;
  userPhone: string;
  isUserReady: boolean;
  needsProfileCompletion: boolean;
  loyaltyConfigLoading: boolean;
  loyaltyStatusLoading: boolean;
  isLoyaltyMember: boolean;
  shouldAutoEnroll: boolean;
  onAutoEnroll: () => void;
  onAuthComplete: () => void;
};

type UseIrlEventAuthReturn = {
  handleAuthSuccess: (options: AuthOptions) => Promise<void>;
  isProcessingAuth: boolean;
};

/**
 * Hook for handling post-auth flow in IRL events.
 * Always creates/updates the DPOS customer record to ensure EcomUserId is written,
 * then fires tracking event and signals auth completion.
 */
export function useIrlEventAuth({
  dispensaryId,
  enterpriseId,
  isoRegionCode,
  userPhone,
  isUserReady,
  needsProfileCompletion,
  loyaltyConfigLoading,
  loyaltyStatusLoading,
  isLoyaltyMember,
  shouldAutoEnroll,
  onAutoEnroll,
  onAuthComplete,
}: UseIrlEventAuthParams): UseIrlEventAuthReturn {
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [pendingAuthEvent, setPendingAuthEvent] = useState<PendingAuthEvent>(null);
  const hasEnsuredDposCustomer = useRef(false);
  const isDispensaryPath = !!dispensaryId;

  const { trackEvent } = useIrlEventTracking();
  const { open: openCompleteAccountModal } = useCompleteAccountModal();
  const { createEnterpriseDposCustomer } = useCreateEnterpriseDposCustomer();

  const ensureDposCustomer = useCallback(async (): Promise<EnsureDposCustomerResult> => {
    if ((isDispensaryPath && !userPhone) || (!isDispensaryPath && (!enterpriseId || !isoRegionCode || !userPhone))) {
      return { success: false, didAutoEnroll: false };
    }

    const result = await createEnterpriseDposCustomer({ isEnrollingLoyalty: shouldAutoEnroll });
    const success = !!result?.posCustomerId;
    const didAutoEnroll = success && shouldAutoEnroll && !!result.isLoyaltyMember;
    return { success, didAutoEnroll };
  }, [isDispensaryPath, enterpriseId, isoRegionCode, userPhone, createEnterpriseDposCustomer, shouldAutoEnroll]);

  const processAuth = useCallback(
    async ({ authEvent, skipNavigation = false }: AuthOptions): Promise<void> => {
      hasEnsuredDposCustomer.current = true;
      setIsProcessingAuth(true);
      try {
        const { success, didAutoEnroll } = await ensureDposCustomer();
        if (!success) {
          return;
        }

        if (didAutoEnroll) {
          onAutoEnroll();
        }

        void trackEvent(authEvent);

        if (!skipNavigation) {
          onAuthComplete();
        }
      } finally {
        setIsProcessingAuth(false);
      }
    },
    [ensureDposCustomer, onAutoEnroll, trackEvent, onAuthComplete]
  );

  const handleAuthSuccess = useCallback(
    async (options: AuthOptions): Promise<void> => {
      if (isProcessingAuth) {
        return;
      }
      if (!isUserReady || loyaltyConfigLoading) {
        setPendingAuthEvent((prev) => prev ?? options);
        return;
      }
      await processAuth(options);
    },
    [isUserReady, loyaltyConfigLoading, isProcessingAuth, processAuth]
  );

  // Process pending auth when user is ready and loyalty config has loaded.
  useEffect(() => {
    if (!pendingAuthEvent || !isUserReady || isProcessingAuth || loyaltyConfigLoading) {
      return;
    }

    const next = pendingAuthEvent;
    setPendingAuthEvent(null);
    void processAuth(next);
  }, [pendingAuthEvent, isUserReady, isProcessingAuth, loyaltyConfigLoading, processAuth]);

  // For users who are already logged in at page load,
  // ensure that a DPOS customer profile is created if needed.
  // Skip if the user is already a loyalty member — they already have a DPOS profile,
  // and calling with isEnrollingLoyalty:false would unenroll them.
  useEffect(() => {
    if (
      !isUserReady ||
      loyaltyConfigLoading ||
      loyaltyStatusLoading ||
      isLoyaltyMember ||
      hasEnsuredDposCustomer.current
    )
      return;
    hasEnsuredDposCustomer.current = true;

    async function ensure(): Promise<void> {
      const { didAutoEnroll } = await ensureDposCustomer();
      if (didAutoEnroll) onAutoEnroll();
    }
    void ensure();
  }, [isUserReady, loyaltyConfigLoading, loyaltyStatusLoading, isLoyaltyMember, ensureDposCustomer, onAutoEnroll]);

  // Open profile completion modal when user needs to complete profile
  useEffect(() => {
    if (!pendingAuthEvent || !needsProfileCompletion) {
      return;
    }
    void openCompleteAccountModal();
  }, [pendingAuthEvent, needsProfileCompletion, openCompleteAccountModal]);

  return {
    handleAuthSuccess,
    isProcessingAuth,
  };
}
